import { CONTACT_EMAIL, PRIVACY_EMAIL } from "./constants";
import { brandHeader, siteFooter, siteShell } from "./shell";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export const PAGE_TITLES = {
  home: "Locket — Private memories & stories",
  privacy: "Privacy Policy — Locket",
  terms: "Terms of Service — Locket",
  contact: "Contact — Locket",
  notFound: "Page not found — Locket",
} as const;

export function renderHome(): string {
  return `
    <div class="page">
      <div class="page-texture" aria-hidden="true"></div>
      <main class="card site-card site-card--wide" role="main">
        ${brandHeader()}
        <div class="site-hero">
          <h1 class="site-title">A private place for the people you love.</h1>
          <p class="site-lead">
            Locket is an iOS app for preserving memories, stories, and voice — inside a personal
            keepsake for each loved one in your life.
          </p>
        </div>

        <section class="site-section" aria-labelledby="what-heading">
          <h2 id="what-heading" class="site-heading">What Locket is</h2>
          <p class="site-body">
            Locket helps families capture what matters: everyday moments, treasured photos,
            reflections, and interview-style <strong>Storybook</strong> prompts answered in a
            loved one&rsquo;s own words.
          </p>
          <p class="site-body">
            Each locket belongs to someone you care about — a parent, grandparent, partner, or
            friend. You write memories <em>about</em> them. They can share stories <em>from</em>
            their own perspective. Everything stays private to you and the people you invite.
          </p>
        </section>

        <section class="site-section" aria-labelledby="storybook-heading">
          <h2 id="storybook-heading" class="site-heading">Storybook invitations</h2>
          <p class="site-body">
            If someone sent you a Storybook link, you can answer their question in your browser —
            no app install required. Open the personal link they shared with you; your words will
            be saved safely to their locket.
          </p>
        </section>

        <section class="site-section site-section--last" aria-labelledby="trust-heading">
          <h2 id="trust-heading" class="site-heading">Private by design</h2>
          <ul class="site-list">
            <li>Memories and Storybook answers are not public social posts.</li>
            <li>You control who receives invitations and who can contribute.</li>
            <li>We use secure cloud storage to sync and protect your content.</li>
          </ul>
        </section>

        <nav class="site-inline-nav" aria-label="Legal and contact">
          <a href="/privacy">Privacy Policy</a>
          <a href="/terms">Terms of Service</a>
          <a href="/contact">Contact</a>
        </nav>
      </main>
      ${siteFooter()}
    </div>
  `;
}

export function renderPrivacy(): string {
  const updated = "June 24, 2026";
  return siteShell(`
    <article class="site-article">
      <h1 class="site-title">Privacy Policy</h1>
      <p class="site-meta">Last updated ${escapeHtml(updated)}</p>

      <p class="site-body">
        Locket (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the Locket iOS app
        and related web pages at <strong>locketapp.com</strong> (including Storybook invitation pages).
        This Privacy Policy explains what information we collect, how we use it, and the choices you have.
      </p>

      <h2 class="site-heading">Summary</h2>
      <ul class="site-list">
        <li>Locket is a private memory-keeping app — not a public social network.</li>
        <li>We do <strong>not</strong> sell your personal information.</li>
        <li>We do <strong>not</strong> use your data for cross-app advertising or tracking.</li>
        <li>You control what you save, who you invite, and whether you use optional features such as location or notifications.</li>
      </ul>

      <h2 class="site-heading">Information we collect</h2>
      <p class="site-body">Depending on how you use Locket, we may process:</p>
      <ul class="site-list">
        <li><strong>Account information</strong> — if you choose <strong>Sign in with Apple</strong>, we receive an account identifier from Apple and, on first sign-in only, the name and email address Apple shares with your consent. We use this to secure your account and sync your content.</li>
        <li><strong>Loved-one profiles</strong> — names, nicknames, dates, relationship details, and profile photos you add for each locket.</li>
        <li><strong>Memories and keepsakes</strong> — stories, notes, dates, tags, reflections, and other content you create about a loved one.</li>
        <li><strong>Media</strong> — photos, videos, audio recordings, and voice notes you choose to add or capture in the app.</li>
        <li><strong>Location information</strong> — if you grant permission, approximate location when the app is in use to show nearby memories on your atlas and to support optional nearby-place reminders. Location is used for app functionality only; we do not sell it or use it for advertising.</li>
        <li><strong>Storybook responses</strong> — written answers (and, in the app, voice answers) to interview prompts, whether submitted in the app or through a Storybook web link.</li>
        <li><strong>Invitations and sharing</strong> — email addresses or contact details you enter to send Storybook links, memory keys, sparks, or other invitations; and content you choose to share with invited people.</li>
        <li><strong>Community stories (ARK)</strong> — if you choose to submit a kindness story for the in-app community feed, we collect the text and optional image you provide. Submissions are reviewed before publication.</li>
        <li><strong>Device and app data</strong> — notification preferences, app settings, and technical data needed to sync content, deliver notifications, and keep the service reliable (for example, sync tokens and error logs that do not include your memory content).</li>
      </ul>

      <h2 class="site-heading">Guest mode vs. signed-in use</h2>
      <p class="site-body">
        You may use Locket as a <strong>guest</strong> without signing in. In guest mode, your locket content
        stays on your device and is not backed up to our cloud services until you sign in.
        If you later sign in with Apple, we associate your account with cloud sync and backup features
        you use while signed in.
      </p>

      <h2 class="site-heading">Permissions you control</h2>
      <ul class="site-list">
        <li><strong>Photos, camera, and microphone</strong> — only when you choose to add or capture media.</li>
        <li><strong>Location</strong> — only when you allow it, for the memory atlas and optional nearby reminders. You can change this anytime in iOS Settings.</li>
        <li><strong>Notifications</strong> — optional reminders for birthdays, memory anniversaries, sparks, invitations, tags, and nearby memories. You can manage these in the Locket app and in iOS Settings.</li>
      </ul>

      <h2 class="site-heading">Storybook web responses</h2>
      <p class="site-body">
        If you open a Storybook link (<code>/story/…</code>) without installing the app, we load the
        prompt associated with that link and save the answer you submit. Responses are <strong>not</strong>
        published publicly. They are delivered to the locket owner who sent the invitation.
        Share links can expire or be revoked by the sender.
      </p>

      <h2 class="site-heading">How we use information</h2>
      <p class="site-body">
        We use information only to operate and improve Locket, including to:
      </p>
      <ul class="site-list">
        <li>Store, display, and sync your lockets and memories across your devices.</li>
        <li>Deliver invitations, sparks, shared memories, Storybook prompts, and responses.</li>
        <li>Authenticate your account and protect against abuse.</li>
        <li>Send notifications you have enabled.</li>
        <li>Review and moderate community story submissions.</li>
        <li>Maintain reliability and security of the service.</li>
      </ul>

      <h2 class="site-heading">How we share information</h2>
      <p class="site-body">
        We do not sell personal information. We share information only in these situations:
      </p>
      <ul class="site-list">
        <li><strong>With people you invite</strong> — when you send a Storybook link, memory key, spark, or other shared content, the recipient can see what you choose to share with them.</li>
        <li><strong>With service providers</strong> — companies that host and process data on our behalf, as described below.</li>
        <li><strong>For legal and safety reasons</strong> — if required by law or to protect users, our rights, or the safety of others.</li>
        <li><strong>Community stories</strong> — if you submit an ARK kindness story and it is approved, the story text and any image you included may appear to other Locket users in the in-app feed.</li>
      </ul>

      <h2 class="site-heading">Service providers</h2>
      <p class="site-body">
        Locket uses trusted providers to run the service. They process data on our behalf under contractual
        safeguards and only as needed to provide Locket:
      </p>
      <ul class="site-list">
        <li><strong>Apple</strong> — Sign in with Apple, iCloud, and CloudKit for account sign-in, sync, sharing, and push notifications related to shared content. Apple&rsquo;s privacy policy applies to Apple&rsquo;s processing: <a href="https://www.apple.com/legal/privacy/" rel="noopener noreferrer">apple.com/legal/privacy</a>.</li>
        <li><strong>Supabase</strong> — authentication, database, and file storage for app features such as Storybook, cloud backup, and community stories. Media and Storybook content may be stored in Supabase Storage.</li>
      </ul>

      <h2 class="site-heading">Data retention</h2>
      <p class="site-body">
        We keep your content for as long as your locket or account exists, unless you delete it or ask us
        to remove it. Deleted content may remain in encrypted backups for a limited period before being
        overwritten. Storybook share links may expire or be revoked by the sender.
      </p>

      <h2 class="site-heading">Your choices and account deletion</h2>
      <p class="site-body">
        You can view, edit, and delete locket content inside the app. Signed-in users can delete their
        account from <strong>Settings → Account → Delete Account</strong>. Account deletion permanently
        removes your Locket account and associated cloud data from our servers. Memories stored locally
        on your device remain until you delete them. Content synced through your personal iCloud account
        may remain until you manage it in iOS Settings.
      </p>
      <p class="site-body">
        You can also email
        <a href="mailto:${escapeHtml(PRIVACY_EMAIL)}">${escapeHtml(PRIVACY_EMAIL)}</a>
        with questions or data requests.
      </p>

      <h2 class="site-heading">Children&rsquo;s privacy</h2>
      <p class="site-body">
        Locket is not directed at children under 13. We do not knowingly collect personal information from
        children under 13. If you believe a child has provided us personal information, contact us and we
        will take steps to delete it.
      </p>

      <h2 class="site-heading">Security</h2>
      <p class="site-body">
        We use industry-standard safeguards, including encryption in transit and access controls on cloud
        services. No method of transmission or storage is completely secure; please use a device passcode
        and keep your Apple account secure.
      </p>

      <h2 class="site-heading">Changes to this policy</h2>
      <p class="site-body">
        We may update this Privacy Policy from time to time. We will post the revised policy on this page
        and update the &ldquo;Last updated&rdquo; date above. Continued use of Locket after changes means
        you accept the updated policy.
      </p>

      <h2 class="site-heading">Contact</h2>
      <p class="site-body">
        Privacy questions or data requests: <a href="mailto:${escapeHtml(PRIVACY_EMAIL)}">${escapeHtml(PRIVACY_EMAIL)}</a><br />
        General inquiries: <a href="mailto:${escapeHtml(CONTACT_EMAIL)}">${escapeHtml(CONTACT_EMAIL)}</a>
      </p>

      <p class="site-back"><a href="/">← Back to home</a></p>
    </article>
  `, { wide: true });
}

export function renderTerms(): string {
  const updated = "June 24, 2026";
  return siteShell(`
    <article class="site-article">
      <h1 class="site-title">Terms of Service</h1>
      <p class="site-meta">Last updated ${escapeHtml(updated)}</p>

      <p class="site-body">
        These Terms of Service (&ldquo;Terms&rdquo;) govern your use of the Locket iOS application and
        related services at <strong>locketapp.com</strong> (collectively, the &ldquo;Service&rdquo;).
        By using the Service, you agree to these Terms. If you do not agree, do not use the Service.
      </p>

      <h2 class="site-heading">1. Who we are</h2>
      <p class="site-body">
        The Service is operated by Locket (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;).
        Contact: <a href="mailto:${escapeHtml(CONTACT_EMAIL)}">${escapeHtml(CONTACT_EMAIL)}</a>.
      </p>

      <h2 class="site-heading">2. Eligibility</h2>
      <p class="site-body">
        You must be at least 13 years old to use the Service. If you are under 18, you represent that
        you have permission from a parent or legal guardian. The Service is not directed to children
        under 13.
      </p>

      <h2 class="site-heading">3. Your account</h2>
      <p class="site-body">
        You may use Locket as a guest or sign in with Apple. You are responsible for activity under
        your account and for keeping your device and Apple ID secure. You may sign out at any time.
        You may permanently delete your account from <strong>Settings → Account → Delete Account</strong>
        in the app, which removes your account and associated cloud data from our servers as described
        in our <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2 class="site-heading">4. Your content</h2>
      <p class="site-body">
        You retain ownership of memories, photos, audio, text, and other content you submit
        (&ldquo;Your Content&rdquo;). You grant us a limited license to host, store, process, back up,
        sync, and display Your Content only as needed to operate the Service — including delivering
        Storybook invitations, sparks, shared memories, and other features you choose to use.
      </p>
      <p class="site-body">
        You represent that you have the rights to upload Your Content and that it does not violate
        these Terms or applicable law.
      </p>

      <h2 class="site-heading">5. Private use and sharing</h2>
      <p class="site-body">
        Locket is designed for private memory-keeping. Content is not public by default. When you invite
        someone or share a link, you control what is shared with that recipient. You are responsible
        for who you invite and what you share.
      </p>

      <h2 class="site-heading">6. Storybook web responses</h2>
      <p class="site-body">
        If you respond to a Storybook invitation through a web link, your response is delivered to the
        locket owner who sent the invitation. Do not submit content you do not have permission to share.
      </p>

      <h2 class="site-heading">7. Community stories (ARK)</h2>
      <p class="site-body">
        If you choose to submit a kindness story to the in-app community feed, you grant us permission
        to review, moderate, and — if approved — display that submission to other Locket users. We may
        remove submissions that violate these Terms or our community standards.
      </p>

      <h2 class="site-heading">8. Acceptable use</h2>
      <p class="site-body">You agree not to:</p>
      <ul class="site-list">
        <li>Use the Service for unlawful, harmful, harassing, or abusive purposes.</li>
        <li>Upload content that infringes others&rsquo; rights or privacy.</li>
        <li>Attempt to access accounts or data that are not yours.</li>
        <li>Interfere with or disrupt the Service, including by automated scraping or abuse.</li>
        <li>Reverse engineer or misuse the Service except as permitted by law.</li>
      </ul>

      <h2 class="site-heading">9. Service changes and availability</h2>
      <p class="site-body">
        We may modify, suspend, or discontinue features at any time. We strive for reliability but do
        not guarantee uninterrupted or error-free operation. Cloud sync and backup require a network
        connection and a valid signed-in account.
      </p>

      <h2 class="site-heading">10. Disclaimers</h2>
      <p class="site-body">
        THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF
        ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
        FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT, TO THE FULLEST EXTENT PERMITTED BY LAW.
      </p>

      <h2 class="site-heading">11. Limitation of liability</h2>
      <p class="site-body">
        TO THE FULLEST EXTENT PERMITTED BY LAW, LOCKET AND ITS OPERATORS WILL NOT BE LIABLE FOR ANY
        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF DATA, PROFITS,
        OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM RELATING
        TO THE SERVICE WILL NOT EXCEED THE GREATER OF USD $50 OR THE AMOUNT YOU PAID US IN THE TWELVE
        MONTHS BEFORE THE CLAIM (IF ANY).
      </p>

      <h2 class="site-heading">12. Termination</h2>
      <p class="site-body">
        You may stop using the Service at any time. We may suspend or terminate access if you violate
        these Terms or if required for legal, security, or operational reasons. Sections that by their
        nature should survive termination will survive.
      </p>

      <h2 class="site-heading">13. Privacy</h2>
      <p class="site-body">
        Our <a href="/privacy">Privacy Policy</a> explains how we collect and use information. It is
        incorporated into these Terms by reference.
      </p>

      <h2 class="site-heading">14. Changes to these Terms</h2>
      <p class="site-body">
        We may update these Terms from time to time. We will post the revised Terms on this page and
        update the &ldquo;Last updated&rdquo; date. Continued use after changes means you accept the
        updated Terms.
      </p>

      <h2 class="site-heading">15. Governing law</h2>
      <p class="site-body">
        These Terms are governed by the laws of the United States and the State of California, without
        regard to conflict-of-law principles, except where mandatory local law applies.
      </p>

      <h2 class="site-heading">16. Contact</h2>
      <p class="site-body">
        Questions about these Terms: <a href="mailto:${escapeHtml(CONTACT_EMAIL)}">${escapeHtml(CONTACT_EMAIL)}</a>
      </p>

      <p class="site-back"><a href="/">← Back to home</a></p>
    </article>
  `, { wide: true });
}

export function renderContact(): string {
  return siteShell(`
    <article class="site-article">
      <h1 class="site-title">Contact</h1>
      <p class="site-body">
        We&rsquo;d love to hear from you — whether you have a question about the Locket app, need
        help with a Storybook invitation link, or want to share feedback.
      </p>

      <div class="site-contact-block">
        <p class="site-contact-label">Email</p>
        <p class="site-contact-value">
          <a href="mailto:${escapeHtml(CONTACT_EMAIL)}">${escapeHtml(CONTACT_EMAIL)}</a>
        </p>
      </div>

      <div class="site-contact-block">
        <p class="site-contact-label">Privacy</p>
        <p class="site-contact-value">
          <a href="mailto:${escapeHtml(PRIVACY_EMAIL)}">${escapeHtml(PRIVACY_EMAIL)}</a>
        </p>
      </div>

      <p class="site-body site-body--spaced">
        If you received a Storybook link, please include the message you were sent (you can omit
        private details). We typically reply within a few business days.
      </p>

      <p class="site-back"><a href="/">← Back to home</a></p>
    </article>
  `);
}

export function renderNotFound(): string {
  return siteShell(`
    <div class="state state--centered">
      <h1 class="state-title">Page not found</h1>
      <p class="state-body">That page doesn&rsquo;t exist. You can return to the Locket home page or open a Storybook link you were sent.</p>
      <p class="site-back site-back--centered"><a href="/">Go to home</a></p>
    </div>
  `);
}
